// The x-axis used to carry a "Date: " prefix baked into every tick label.
// That's now shown once, externally, as the axis title instead (see
// `xaxis.title` on each chart's options) — this just normalizes the raw
// value.
export const formatDateAxisLabel = value => {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

export const getEffectiveUnit = (records, fallback = '') => {
  const record = (records || []).find(item => item?.effective_unit)
  return record?.effective_unit || fallback || ''
}

export const formatEffectiveUnitLabel = effectiveUnit => (
  effectiveUnit ? `Effective unit: ${effectiveUnit}` : ''
)

// `Math.min(...array)`/`Math.max(...array)` blow the call stack
// (RangeError: Maximum call stack size exceeded) once `array` gets large —
// a real risk here since it's built from every visible data point across
// every displayed country/series. A plain reduce has no such limit.
export const minMax = values => {
  let min = Infinity
  let max = -Infinity
  for (const value of values) {
    if (value < min) min = value
    if (value > max) max = value
  }
  return [min, max]
}

// ApexCharts x-range annotations (x/x2) always anchor their `label` to the
// x1 edge — there's no "center it in the range" option, which is why a
// highlight's text used to show up pinned to one side instead of centered
// in the shaded section. The fix is to not use a range-annotation label at
// all: render the text as its own zero-size *point* annotation placed at
// the horizontal midpoint of the section (by category index, since the
// x-axis is categorical) and at a y value near the vertical middle of the
// chart — that's what actually centers it, both horizontally in the
// section and vertically in the chart, with the text itself rendered
// normally (horizontal, not squeezed against an edge).
export const buildHighlightLabelPoints = (highlightItems, categoriesRaw, formatCategory, yMin, yMax) => {
  const domainMin = Number.isFinite(yMin) ? yMin : 0
  const domainMax = Number.isFinite(yMax) ? yMax : 0
  // Anchored to the edge of the visible range, not the middle — a small
  // inset (5% of the span) keeps the text/pill from getting clipped by the
  // plot border while still reading as "all the way at the top/bottom".
  const inset = (domainMax - domainMin || 1) * 0.05

  return (highlightItems || [])
    .filter(item => item?.text)
    .map(item => {
      const idx1 = categoriesRaw.indexOf(item.x1?.value)
      const idx2 = categoriesRaw.indexOf(item.x2?.value)
      const midIndex = idx1 !== -1 && idx2 !== -1
        ? Math.round((idx1 + idx2) / 2)
        : (idx1 !== -1 ? idx1 : idx2)
      const midRaw = (midIndex !== -1 && midIndex < categoriesRaw.length)
        ? categoriesRaw[midIndex]
        : (item.x1?.value || item.x2?.value)
      const isBottom = item.textPosition === 'bottom'
      const y = isBottom ? domainMin + inset : domainMax - inset

      return {
        x: formatCategory(midRaw),
        y,
        marker: { size: 0 },
        label: {
          borderWidth: 0,
          borderRadius: 4,
          textAnchor: 'middle',
          // ApexCharts draws a point label ABOVE its point by default —
          // fine near the bottom edge (pushes it inward, up into the plot),
          // but wrong near the top edge (pushes it further up, off/out of
          // the plot). Nudge inward instead: down for a top-anchored label,
          // up for a bottom-anchored one.
          offsetY: isBottom ? -6 : 12,
          style: {
            background: item.color || '#B6B6B6',
            color: '#ffffff',
            fontFamily: 'Open Sans',
            fontSize: '11px',
            padding: { left: 6, right: 6, top: 3, bottom: 3 },
          },
          text: item.text,
        },
      }
    })
}
